/**
 * src/data/FocusTimeBank.js
 * Hardcoded scenario bank for Module 6: Focus & Time Management (Chronos Engine)
 */

export const chronosBank = [
  {
    "id": "chronos_01",
    "distractionSource": "Slack DM (Peer)",
    "messageText": "Hey! Quick question, do you prefer the blue or green logo for the internal newsletter?",
    "optimalTriage": "Ignore/Mute",
    "rationale": "A classic micro-interruption with zero operational urgency. Breaking a deep-work text generation state to answer a subjective design question destroys your flow."
  },
  {
    "id": "chronos_02",
    "distractionSource": "Email (Direct Manager)",
    "messageText": "Can you pull the Q3 variance numbers for me? No rush, just need them by tomorrow morning.",
    "optimalTriage": "Schedule/Buffer",
    "rationale": "The manager explicitly stated 'no rush.' Dropping your current high-priority task to do this immediately is falling into the urgency trap. Route to a later time block."
  },
  {
    "id": "chronos_03",
    "distractionSource": "Automated System Alert",
    "messageText": "CRITICAL: The massive client presentation file you are working in has become corrupted and is failing to auto-save.",
    "optimalTriage": "Immediate Action",
    "rationale": "Severity-1 operational threat. This is the rare exception where abandoning the deep-work task is mandatory to prevent catastrophic loss of your current work."
  },
  {
    "id": "chronos_04",
    "distractionSource": "Slack Channel (#general)",
    "messageText": "@here There are donuts in the breakroom! Come get them before they're gone!",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Breaking deep cognitive focus for sugar is a massive loss of executive function. The context-switching penalty vastly outweighs the reward."
  },
  {
    "id": "chronos_05",
    "distractionSource": "Email (Automated)",
    "messageText": "Your weekly screen-time report is ready to view.",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Algorithmic noise. Clicking this notification during a deep work session provides zero immediate value and invites a spiral of distracted browsing."
  },
  {
    "id": "chronos_06",
    "distractionSource": "Slack DM (Junior Colleague)",
    "messageText": "Hey, how do I reset my password for the company portal again? I forgot the link.",
    "optimalTriage": "Schedule/Buffer",
    "rationale": "While the colleague is blocked, answering IT support questions is not your job. Buffer this to your 'admin' block so you don't reward learned helplessness."
  },
  {
    "id": "chronos_07",
    "distractionSource": "Automated Alert",
    "messageText": "CRITICAL INCIDENT: The company's primary website has crashed and customers cannot buy anything. Revenue loss active.",
    "optimalTriage": "Immediate Action",
    "rationale": "Active revenue loss is a superseding event. Deep work must be suspended immediately to triage a critical financial outage."
  },
  {
    "id": "chronos_08",
    "distractionSource": "Email (CC'd)",
    "messageText": "FYI: The vendor sent over the revised marketing brochures. No action needed on your end.",
    "optimalTriage": "Ignore/Mute",
    "rationale": "The sender literally typed 'No action needed.' Reading this email during a sprint is a complete waste of cognitive bandwidth."
  },
  {
    "id": "chronos_09",
    "distractionSource": "Calendar Notification",
    "messageText": "Starts in 10 minutes: Optional 'Culture Committee' Brainstorming Sync.",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Abandoning a high-priority strategic report to attend an 'optional' brainstorming session is a catastrophic misallocation of executive time."
  },
  {
    "id": "chronos_10",
    "distractionSource": "Slack DM (VP of Sales)",
    "messageText": "Hey, I know you're busy, but can you throw together a quick slide on Q2 metrics for my call at 4 PM?",
    "optimalTriage": "Schedule/Buffer",
    "rationale": "Executive requests feel urgent, but this is for 4 PM. Do not drop your current deep work. Buffer it to complete immediately after your current focus sprint."
  },
  {
    "id": "chronos_11",
    "distractionSource": "Project Tracker Notification",
    "messageText": "Task #4029 Status Changed: 'In Review' to 'Done'.",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Automated status updates are asynchronous ledger entries. They require zero immediate human intervention."
  },
  {
    "id": "chronos_12",
    "distractionSource": "Slack Channel (#random)",
    "messageText": "Did anyone see the finale of that new show last night? So crazy!",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Watercooler gossip. Engaging with this during a deep-work block destroys your flow state for absolutely zero professional return."
  },
  {
    "id": "chronos_13",
    "distractionSource": "Email (Client)",
    "messageText": "We loved the initial pitch! Can we schedule a 15-minute follow-up call sometime next week?",
    "optimalTriage": "Schedule/Buffer",
    "rationale": "High value, low urgency. The client wants to meet 'next week.' Do not break focus now to play calendar ping-pong; batch it for your afternoon admin sweep."
  },
  {
    "id": "chronos_14",
    "distractionSource": "Customer Support Escalation",
    "messageText": "URGENT VIP CLIENT: Their entire account was accidentally deleted. They are threatening a lawsuit.",
    "optimalTriage": "Immediate Action",
    "rationale": "Massive corporate liability and churn risk. This supersedes internal reporting and requires immediate crisis management."
  },
  {
    "id": "chronos_15",
    "distractionSource": "LinkedIn Notification",
    "messageText": "John Doe has a new work anniversary! Congratulate them.",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Social media dopamine bait. Breaking a strategic work block to engage with external platform gamification is the definition of the urgency trap."
  },
  {
    "id": "chronos_16",
    "distractionSource": "Slack DM (Peer)",
    "messageText": "Do you want to grab lunch at noon or 12:30 today?",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Logistical trivia. They can wait 45 minutes for an answer while you finish your high-leverage cognitive task."
  },
  {
    "id": "chronos_17",
    "distractionSource": "Email (Internal Newsletter)",
    "messageText": "Company Update: Welcome to the new Q3 hires and check out our new wellness initiative!",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Internal marketing fluff. It is asynchronous reading material designed for downtime, not a reason to interrupt deep work."
  },
  {
    "id": "chronos_18",
    "distractionSource": "Slack Channel (#general)",
    "messageText": "@channel I'm getting an error message when trying to log into the main company portal, anyone else?",
    "optimalTriage": "Schedule/Buffer",
    "rationale": "Unless you are the on-call IT support specialist, this is a crowdsourced debugging question. Do not derail your report to troubleshoot for someone else."
  },
  {
    "id": "chronos_19",
    "distractionSource": "Automated Security Alert",
    "messageText": "ACTION REQUIRED: Unknown device logged into your corporate email from St. Petersburg, Russia.",
    "optimalTriage": "Immediate Action",
    "rationale": "Active cyber threat to your identity and company data. Deep work must be paused instantly to lock down the compromised account."
  },
  {
    "id": "chronos_20",
    "distractionSource": "Email (Recruiter)",
    "messageText": "We are looking for a highly driven VP of Strategy. Are you open to new opportunities?",
    "optimalTriage": "Ignore/Mute",
    "rationale": "External distraction. Reading recruiting spam while on the clock destroys focus and provides zero value to your current employer."
  },
  {
    "id": "chronos_21",
    "distractionSource": "Slack DM (Manager)",
    "messageText": "Just read your draft for the introduction. Looks great, keep going!",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Positive reinforcement is nice, but it requires zero action. Do not break your typing rhythm to reply with a 'thanks' emoji."
  },
  {
    "id": "chronos_22",
    "distractionSource": "Google Drive Alert",
    "messageText": "Sarah has requested edit access to the 'Q2_Marketing_Budget.xlsx' file.",
    "optimalTriage": "Schedule/Buffer",
    "rationale": "An administrative permission request. While Sarah needs access, it is not an emergency. Buffer this into a 5-minute batch of admin tasks later."
  },
  {
    "id": "chronos_23",
    "distractionSource": "Email (Software Vendor)",
    "messageText": "Your team's software subscription will auto-renew in 7 days.",
    "optimalTriage": "Schedule/Buffer",
    "rationale": "A financial notification that requires review to prevent software budget waste, but with a 7-day runway, it is the opposite of an immediate emergency."
  },
  {
    "id": "chronos_24",
    "distractionSource": "Slack Channel (#sales-wins)",
    "messageText": "🎉 BOOM! Just closed the massive enterprise deal with Acme Corp! 🎉",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Celebratory noise. While great for company morale, stopping a critical report to post party emojis is a failure of time management."
  },
  {
    "id": "chronos_25",
    "distractionSource": "Legal Dept Escalation",
    "messageText": "CEASE AND DESIST: A competitor just filed a massive lawsuit. Stop all work on the Alpha Project immediately until further notice.",
    "optimalTriage": "Immediate Action",
    "rationale": "A direct legal mandate halting operations. Continuing to work on the report could expose the company to massive legal liability."
  },
  {
    "id": "chronos_26",
    "distractionSource": "Calendar Notification",
    "messageText": "Reminder: 'Focus Time' block ends in 15 minutes.",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Meta-distraction. A notification telling you that you are currently focusing is useless. Dismiss it instantly to maintain the flow state."
  },
  {
    "id": "chronos_27",
    "distractionSource": "Email (Cross-Functional Peer)",
    "messageText": "Can you review these draft presentation slides? I'd love your feedback by EOD if possible.",
    "optimalTriage": "Schedule/Buffer",
    "rationale": "A standard collaborative request with an End of Day deadline. Do not let their timeline dictate your current sprint. Buffer it for the afternoon."
  },
  {
    "id": "chronos_28",
    "distractionSource": "Slack DM (CEO)",
    "messageText": "Hey, I saw that article you linked last week. Really interesting take on AI.",
    "optimalTriage": "Ignore/Mute",
    "rationale": "The 'Executive Trap.' Because the CEO sent it, the instinct is to reply instantly. But it's casual chatter. Protect your focus; the CEO will respect the output more than a fast reply."
  },
  {
    "id": "chronos_29",
    "distractionSource": "IT Network Alert",
    "messageText": "MALWARE DETECTED: A malicious virus is actively deleting all the files on your computer. Disconnect from the internet immediately.",
    "optimalTriage": "Immediate Action",
    "rationale": "A terminal cybersecurity event. The machine must be physically isolated immediately to prevent enterprise-wide data destruction."
  },
  {
    "id": "chronos_30",
    "distractionSource": "Email (Industry Newsletter)",
    "messageText": "Top 10 Trends in Corporate Strategy for Q4.",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Reading thought-leadership blogs during a designated execution block is a common form of productive procrastination. Mute it."
  },
  {
    "id": "chronos_31",
    "distractionSource": "Slack Channel (#watercooler)",
    "messageText": "Who's winning the fantasy football league this week?",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Pure digital clutter. Engaging with fantasy sports while on a tight deadline for a strategic report is a severe lapse in judgment."
  },
  {
    "id": "chronos_32",
    "distractionSource": "Email (Finance Dept)",
    "messageText": "Reminder: All Q3 expense reports must be submitted by Friday at 5 PM.",
    "optimalTriage": "Schedule/Buffer",
    "rationale": "A vital administrative compliance task with a delayed deadline. Add it to your end-of-week task list, but do not stop writing to find your receipts."
  },
  {
    "id": "chronos_33",
    "distractionSource": "Slack DM (Direct Report)",
    "messageText": "I'm stuck on this Excel formula, it keeps giving me an error. Can we jump on a call?",
    "optimalTriage": "Schedule/Buffer",
    "rationale": "The 'Reverse Delegation Trap.' Do not break your deep work to debug a junior's spreadsheet. Buffer the request and force them to try solving it themselves first."
  },
  {
    "id": "chronos_34",
    "distractionSource": "Internal Chat DM (Colleague)",
    "messageText": "Got a second to look at this visual design draft?",
    "optimalTriage": "Schedule/Buffer",
    "rationale": "A classic subjective interruption. Politely but firmly defer to an asynchronous review or a later scheduled block."
  },
  {
    "id": "chronos_35",
    "distractionSource": "PR Crisis Alert",
    "messageText": "URGENT: Our CEO just tweeted something highly offensive and the stock is tanking. All hands on deck.",
    "optimalTriage": "Immediate Action",
    "rationale": "A catastrophic brand and financial event. Normal operations cease immediately to contain the existential threat to the company."
  },
  {
    "id": "chronos_36",
    "distractionSource": "Email (Automated CRM System)",
    "messageText": "Report: 'Weekly Pipeline Velocity' has been generated.",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Automated data dumps should be reviewed on a set cadence (e.g., Monday mornings). Opening them mid-sprint is a distraction trap."
  },
  {
    "id": "chronos_37",
    "distractionSource": "Slack Channel (#design-feedback)",
    "messageText": "Can everyone vote on which font we should use for the new landing page?",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Design by committee via Slack poll. This is a low-stakes, subjective request that does not warrant breaking cognitive momentum."
  },
  {
    "id": "chronos_38",
    "distractionSource": "Email (HR Dept)",
    "messageText": "Please complete your mandatory anti-harassment training module by the end of the month.",
    "optimalTriage": "Schedule/Buffer",
    "rationale": "Mandatory compliance, but with a multi-week runway. Schedule a dedicated block for corporate training later in the month."
  },
  {
    "id": "chronos_39",
    "distractionSource": "Slack DM (Peer)",
    "messageText": "Are you going to the happy hour after work today?",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Social logistics. Replying disrupts your flow state for a conversation that can easily wait until 5 PM."
  },
  {
    "id": "chronos_40",
    "distractionSource": "Facilities Alert",
    "messageText": "FIRE ALARM: Please evacuate the building immediately via the nearest stairwell.",
    "optimalTriage": "Immediate Action",
    "rationale": "Literal life-safety emergency. Abandon the computer and evacuate."
  },
  {
    "id": "chronos_41",
    "distractionSource": "Email (Client - Low Priority)",
    "messageText": "Just checking in, did you get my email from yesterday about the font size?",
    "optimalTriage": "Schedule/Buffer",
    "rationale": "The 'Anxious Client' trap. Dropping everything to reassure them reinforces their impatience. Buffer the reply for your designated email block."
  },
  {
    "id": "chronos_42",
    "distractionSource": "Slack DM (IT Support)",
    "messageText": "We need to push a mandatory update to your laptop, it will force a restart in 60 seconds.",
    "optimalTriage": "Immediate Action",
    "rationale": "A forced hardware interruption. You must immediately save your work and prepare for the restart, or lose your progress entirely."
  },
  {
    "id": "chronos_43",
    "distractionSource": "Email (Spam/Phishing)",
    "messageText": "URGENT: Your corporate email password has expired. Click here to reset.",
    "optimalTriage": "Ignore/Mute",
    "rationale": "A phishing attempt disguised as urgency. Do not click. Flag as spam and ignore entirely to protect company systems."
  },
  {
    "id": "chronos_44",
    "distractionSource": "Slack Channel (#marketing)",
    "messageText": "Here is the final PDF of the Q1 report if anyone wants to read it.",
    "optimalTriage": "Schedule/Buffer",
    "rationale": "Good reference material for later, but reading an entire finished report right now while you are trying to write your own is a massive distraction."
  },
  {
    "id": "chronos_45",
    "distractionSource": "Calendar Notification",
    "messageText": "Your PTO request for next Friday has been approved by your manager.",
    "optimalTriage": "Ignore/Mute",
    "rationale": "An administrative confirmation requiring zero action. Acknowledge internally and dismiss instantly to maintain typing flow."
  },
  {
    "id": "chronos_46",
    "distractionSource": "Email (Industry Competitor)",
    "messageText": "We just launched our new feature set! See what you're missing.",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Competitor marketing emails will trigger anxiety, causing you to doom-scroll their website instead of finishing your own strategic work. Delete it."
  },
  {
    "id": "chronos_47",
    "distractionSource": "Slack DM (VP of Operations)",
    "messageText": "I need to review your draft of this report before you send it to the board. Send me what you have so far.",
    "optimalTriage": "Schedule/Buffer",
    "rationale": "A crucial alignment request, but sending a half-finished draft breaks your momentum. Reply: 'Will send you the complete draft in 45 mins' and keep writing."
  },
  {
    "id": "chronos_48",
    "distractionSource": "Compliance Alert",
    "messageText": "DATA BREACH: An email was just accidentally sent to the entire company containing everyone's salaries and confidential bonuses.",
    "optimalTriage": "Immediate Action",
    "rationale": "A catastrophic HR and privacy leak. This is a massive internal crisis that requires total, immediate mobilization to contain the fallout."
  },
  {
    "id": "chronos_49",
    "distractionSource": "Email (Office Manager)",
    "messageText": "The fridge is being cleaned out at 4 PM today. Please remove your Tupperware.",
    "optimalTriage": "Ignore/Mute",
    "rationale": "The definition of trivial office noise. Your strategic report is worth infinitely more than old leftovers. Ignore it entirely."
  },
  {
    "id": "chronos_50",
    "distractionSource": "Slack Channel (#general)",
    "messageText": "Can everyone welcome the new marketing intern, Chad?",
    "optimalTriage": "Ignore/Mute",
    "rationale": "Pleasantries belong in the breaks between deep work sessions. Breaking focus to post a waving-hand emoji diminishes your cognitive output."
  }
];